import React from 'react'
import {
    VIEW_SIZE,
    CENTER,
    CENTER_RADIUS,
    sectorDefs,
    ringLabels,
    axisLabels,
    itemsInCell,
    cellDescriptor,
    cellsEqual,
} from '../lib/geometry'
import { colorFor, BODY_FONT, HEAD_FONT } from '../lib/palette'

const SHOW_RING_LABELS = true

const SectorMap = ({ palette, items, selectedCell, onSelectCell, centerSub }) => {
    const styles = {
        chartWrap: { display: 'flex', justifyContent: 'center', padding: '5px 0' },
        chartInner: { position: 'relative', width: '100%', maxWidth: '620px' },
        svg: { width: '100%', height: 'auto', display: 'block' },
        labelOverlay: { position: 'absolute', inset: 0, pointerEvents: 'none' },
        centerLabelWrap: { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' },
        centerLabel: { fontFamily: HEAD_FONT, fontWeight: 600, fontSize: '22px', color: palette.ink },
        centerSub: { fontFamily: BODY_FONT, fontSize: '11px', color: palette.sub },
        ringLabel: { fontFamily: BODY_FONT, fontSize: '12px', fontWeight: 500, fill: palette.sub, letterSpacing: '0.02em' },
        axisLabel: { fontFamily: BODY_FONT, fontSize: '11px', fontWeight: 600, fill: palette.sub, letterSpacing: '0.12em' },
    }

    const countLabelStyle = s => ({
        position: 'absolute',
        left: `${((s.labelX / VIEW_SIZE) * 100).toFixed(3)}%`,
        top: `${((s.labelY / VIEW_SIZE) * 100).toFixed(3)}%`,
        transform: 'translate(-50%,-50%)',
        fontFamily: BODY_FONT,
        fontSize: '15px',
        fontWeight: 600,
        color: palette.ink,
        pointerEvents: 'none',
    })

    const sectors = sectorDefs.map(s => ({
        ...s,
        count: itemsInCell(items, s.cell).length,
        selected: cellsEqual(s.cell, selectedCell),
    }))
    // Paint the selected sector last so its highlight stroke is not covered by neighbouring sectors.
    const paintOrder = selectedCell ? [...sectors].sort((a, b) => Number(a.selected) - Number(b.selected)) : sectors

    const activate = cell => () => onSelectCell(cell)
    const keyActivate = cell => e => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault()
            onSelectCell(cell)
        }
    }

    return (
        <div style={styles.chartWrap}>
            <div style={styles.chartInner}>
                <svg viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} style={styles.svg}>
                    {paintOrder.map(s => (
                        <path
                            key={s.id}
                            d={s.d}
                            fill={colorFor(s.cell.valence, s.cell.level, palette)}
                            stroke={palette.bg}
                            strokeWidth="2"
                            className={s.selected ? 'sector sector--selected' : 'sector'}
                            role="button"
                            tabIndex={0}
                            aria-label={`${cellDescriptor(s.cell)} — ${s.count} ${s.count === 1 ? 'reflection' : 'reflections'}`}
                            onClick={activate(s.cell)}
                            onKeyDown={keyActivate(s.cell)}
                        />
                    ))}
                    <circle cx={CENTER} cy={CENTER} r={CENTER_RADIUS} fill={palette.panelBg} stroke={palette.line} strokeWidth="1" />
                    {SHOW_RING_LABELS && (
                        <>
                            {ringLabels.map(l => (
                                <text key={l.text} x={l.x} y={l.y} dominantBaseline="middle" textAnchor="middle" style={styles.ringLabel}>
                                    {l.text}
                                </text>
                            ))}
                            {axisLabels.map(l => (
                                <text key={l.text} x={l.x} y={l.y} textAnchor="middle" style={styles.axisLabel}>
                                    {l.text}
                                </text>
                            ))}
                        </>
                    )}
                </svg>
                <div style={styles.labelOverlay}>
                    <div style={styles.centerLabelWrap}>
                        <div style={styles.centerLabel}>Now</div>
                        <div style={styles.centerSub}>{centerSub}</div>
                    </div>
                    {sectors.map(s => (
                        <div key={s.id} style={countLabelStyle(s)} aria-hidden="true">
                            {s.count > 0 ? String(s.count) : ''}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default SectorMap
